import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { LayerState, TWC, VectorLayerState } from '@tmapy/beetlejuice-utils';
import {
  deriveMapStateFromConfig,
  deriveMapSearchStateFromConfig,
  INTERACTION,
} from '@tmapy/beetlejuice-utils';
import { useSelector, useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  BtnBarSpan,
  CtaBtn,
  MultiSelectProps,
  SecondaryBtn,
  SvgSearch,
  SvgArrowRight,
  SvgArrowLeft,
  WarningAlert,
} from '@tmapy/style-guide';
import { MultiSelect, FormItem, RadioGroup } from '@tmapy/style-guide';
import { InteractionDraw, InteractionSelect, getArea } from '@tmapy/mapcore';
import type { MapSearchState } from '@tmapy/beetlejuice-utils';
import { actionSetMapSearchState } from '@tmapy/map-search';
import {
  actionSetMapSwitcherState,
  deriveStateFromConfig as deriveMapSwitcherStateFromConfig,
} from '@tmapy/map-switcher';

import './Areas.scss';

import type {
  ORPListResponse,
  CadasterListResponse,
  ObecListResponse,
  KrajListResponse,
  MomcListResponse,
} from '../../openapi/types';
import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';
import { OrderTypeEnum } from '../../openapi/types/OrderTypeEnum';

import { mapConfig } from '../../areas/mapConfig';
import { Edit } from '../../areas/components/Edit';
import { Zoom } from '../../map/components/Zoom';
import { MapSwitcher } from '../../map/components/MapSwitcher';
import { MapWithStore } from '../../map/components/MapWithStore';
import { UNMANAGED_AREA_LAYERS } from '../../areas/constants';
import { APP_TYPE } from '../../constants';

import { ORDER_REGION_TYPES } from '../constants';
import { actionSetAreaType, actionSetOverLimitArea } from '../actions/newOrder';
import { isSinglePolygonArea } from '../utils/isSinglePolygonArea';
import { newOrderMessages, orderMessages } from './orderMessages';
import { SearchPanel } from './SearchPanel';
import { MapToolbar } from './MapToolbar';
import { NewOrderShpDgnAttachment } from './NewOrderShpDgnAttachment';

type AreasProps = {
  cadaster: MultiSelectProps<CadasterListResponse>;
  city: MultiSelectProps<ObecListResponse>;
  orp: MultiSelectProps<ORPListResponse>;
  region: MultiSelectProps<KrajListResponse>;
  momc: MultiSelectProps<MomcListResponse>;
  onPrevStepClick(): void;
  onNextStepClick(): void;
  onChange(clearAreaType: boolean): void;
};

export const Areas: React.FC<AreasProps> = ({
  cadaster,
  city,
  orp,
  region,
  momc,
  onPrevStepClick,
  onNextStepClick,
  onChange,
}) => {
  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const interaction = useSelector((state) => state.mapCore.interaction);
  const { customerType, regionCodes } = useSelector((state) => state.app.config);
  const config = useSelector((state) => state.app.config.vydej) ?? {};
  const { tabs } = useSelector((state) => state.mapSearch);
  const { selection_type, order_type, areaType, isOverLimitArea, from_primary_db } = useSelector(
    (state) => state.newOrder,
  );

  const [isSearchOpen, setSearchOpen] = useState(false);

  const layers = useSelector((state) => state.mapCore.layers);
  const editMapLayerFeatures = useMemo(() => {
    return (
      (
        layers.find(
          (layer: LayerState) => layer.id === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS,
        ) as VectorLayerState
      )?.features ?? []
    );
  }, [layers]);

  const areaTypes: Record<ORDER_REGION_TYPES, { label: string; value: ORDER_REGION_TYPES }> =
    useMemo(
      () => ({
        [ORDER_REGION_TYPES.CADASTER]: {
          label: formatMessage(orderMessages.cadaster),
          value: ORDER_REGION_TYPES.CADASTER,
        },
        [ORDER_REGION_TYPES.OBEC]: {
          label: formatMessage(orderMessages.city),
          value: ORDER_REGION_TYPES.OBEC,
        },
        [ORDER_REGION_TYPES.MOMC]: {
          label: formatMessage(orderMessages.momc),
          value: ORDER_REGION_TYPES.MOMC,
        },
        [ORDER_REGION_TYPES.ORP]: {
          label: formatMessage(orderMessages.orp),
          value: ORDER_REGION_TYPES.ORP,
        },
        [ORDER_REGION_TYPES.KRAJ]: {
          label: formatMessage(orderMessages.region),
          value: ORDER_REGION_TYPES.KRAJ,
        },
        [ORDER_REGION_TYPES.CUSTOM]: {
          label: formatMessage(newOrderMessages.map),
          value: ORDER_REGION_TYPES.CUSTOM,
        },
        [ORDER_REGION_TYPES.SHPDGN]: {
          label: formatMessage(newOrderMessages.shpdgn),
          value: ORDER_REGION_TYPES.SHPDGN,
        },
      }),
      [formatMessage],
    );

  const handleSearchCloseBtnClick = () => {
    setSearchOpen(false);
  };

  const search = regionCodes ? { addDefaultTabs: true, regionCodes } : undefined;

  const handleSearchBtnClick = () => {
    if (tabs.length === 0 && regionCodes) {
      const state = deriveMapSearchStateFromConfig(search) as MapSearchState;
      dispatch(actionSetMapSearchState(state));
    }

    setSearchOpen(true);
  };

  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig(), search } as TWC), []);

  useEffect(() => {
    dispatch(actionSetMapSwitcherState(deriveMapSwitcherStateFromConfig(config.mapSwitcher)));
  }, [dispatch, config.mapSwitcher]);

  if (!mapState) {
    return null;
  }

  const handleAreaTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(actionSetAreaType(e.target.value));
      onChange(false);
    },
    [dispatch, onChange],
  );

  const areaTypeItems = useMemo(() => {
    // GP
    if (customerType === 'gp' || customerType === 'ipr') {
      const gpRegionTypes = [
        ...(config.areaTypes ?? [
          ORDER_REGION_TYPES.CADASTER,
          ORDER_REGION_TYPES.OBEC,
          ORDER_REGION_TYPES.ORP,
          ORDER_REGION_TYPES.KRAJ,
        ]),
        ORDER_REGION_TYPES.CUSTOM,
      ];
      if (config.shpDgnUpload) gpRegionTypes.push(ORDER_REGION_TYPES.SHPDGN);

      return gpRegionTypes.map((item) => areaTypes[item]);
    }

    // DTM
    if (from_primary_db || selection_type === OrderSelectionTypesEnum.CUSTOM) {
      // CUSTOM | FROM_PRIMARY_DB
      const dtmCustomRegionTypes = [
        ORDER_REGION_TYPES.CADASTER,
        ORDER_REGION_TYPES.OBEC,
        ORDER_REGION_TYPES.ORP,
        ORDER_REGION_TYPES.KRAJ,
        ORDER_REGION_TYPES.CUSTOM,
      ];
      if (config.shpDgnUpload) dtmCustomRegionTypes.push(ORDER_REGION_TYPES.SHPDGN);

      return dtmCustomRegionTypes.map((item) => areaTypes[item]);
    } else if (order_type === OrderTypeEnum.STATE) {
      // PACKAGE/STATE
      return [ORDER_REGION_TYPES.MOMC, ORDER_REGION_TYPES.OBEC, ORDER_REGION_TYPES.KRAJ].map(
        (item) => areaTypes[item],
      );
    } else {
      // PACKAGE/CHANGE
      return [ORDER_REGION_TYPES.KRAJ].map((item) => areaTypes[item]);
    }
  }, [areaTypes, customerType, order_type, selection_type, from_primary_db]);

  // reakce na geometrii
  useEffect(() => {
    if (!editMapLayerFeatures.length || !config.areaLimitHa) {
      // clear warning
      if (isOverLimitArea) {
        dispatch(actionSetOverLimitArea(false));
      }
      return;
    }

    const area = getArea(editMapLayerFeatures);
    // /1e4 - Plocha v hektarech
    if (area / 1e4 > config.areaLimitHa) {
      dispatch(actionSetOverLimitArea(true));
    } else {
      dispatch(actionSetOverLimitArea(false));
    }
  }, [editMapLayerFeatures, config.areaLimitHa]);

  const isNotSinglePolygonArea = useMemo(
    () => !isSinglePolygonArea(editMapLayerFeatures),
    [editMapLayerFeatures],
  );

  return (
    <div className='sg-a-h-100% sg-a-d-f sg-a-fd-c sg-a-g-2 sg-a-pb-2'>
      <div className='sg-u-box sg-a-f-n sg-a-p-2 sg-u-vs-2 dmx-newOrderAreas--form'>
        <RadioGroup
          value={areaType}
          direction='row'
          items={areaTypeItems}
          onChange={handleAreaTypeChange}
        />
        {areaType === ORDER_REGION_TYPES.CADASTER && (
          <FormItem {...cadaster}>
            <MultiSelect {...cadaster} />
          </FormItem>
        )}
        {areaType === ORDER_REGION_TYPES.OBEC && (
          <FormItem {...city}>
            <MultiSelect {...city} />
          </FormItem>
        )}
        {areaType === ORDER_REGION_TYPES.MOMC && (
          <FormItem {...momc}>
            <MultiSelect {...momc} />
          </FormItem>
        )}
        {areaType === ORDER_REGION_TYPES.ORP && (
          <FormItem {...orp}>
            <MultiSelect {...orp} />
          </FormItem>
        )}
        {areaType === ORDER_REGION_TYPES.KRAJ && (
          <FormItem {...region}>
            <MultiSelect {...region} />
          </FormItem>
        )}
        {areaType === ORDER_REGION_TYPES.SHPDGN && (
          <FormItem>
            <div className='sg-a-mt-4'>
              <NewOrderShpDgnAttachment />
            </div>
          </FormItem>
        )}
      </div>
      {isOverLimitArea && (
        <WarningAlert>
          <FormattedMessage {...newOrderMessages.overLimitArea} />
        </WarningAlert>
      )}
      {isNotSinglePolygonArea && (
        <WarningAlert>
          <FormattedMessage {...newOrderMessages.notSinglePolygonArea} />
        </WarningAlert>
      )}
      <div className='sg-a-f-a dmx-newOrderAreas--map'>
        <MapWithStore state={mapState} dispatch={dispatch}>
          {interaction.type === INTERACTION.DRAW && (
            <InteractionDraw
              key={interaction.layerId}
              type={interaction.drawGeometryType}
              geometryLayout={interaction.geometryLayout}
              layerId={interaction.layerId}
              style={interaction.style}
              modify={true}
              dispatch={dispatch}
            />
          )}
          {interaction.type === INTERACTION.SELECT && (
            <InteractionSelect
              layerId={UNMANAGED_AREA_LAYERS.CUSTOM_AREAS}
              dispatch={dispatch}
              style={'ACTIVE'}
            />
          )}
          <div className='dmx-newOrderAreas--search'>
            {isSearchOpen && <SearchPanel onHideBtnClick={handleSearchCloseBtnClick} />}
          </div>
          <div className='dmx-newOrderAreas--tools'>
            <MapToolbar>
              {!isSearchOpen && (
                <SecondaryBtn
                  icon={{ element: <SvgSearch /> }}
                  size='sml'
                  onClick={handleSearchBtnClick}
                >
                  <FormattedMessage {...orderMessages.searchTitle} />
                </SecondaryBtn>
              )}
              {areaType === ORDER_REGION_TYPES.CUSTOM && <Edit />}
            </MapToolbar>
          </div>
          <div className='dmx-newOrderAreas--rightBar'>
            <div className='dmx-newOrderAreas--zoom'>
              <MapToolbar>
                <Zoom />
              </MapToolbar>
            </div>
            <div className='dmx-newOrderAreas--mapSwitcher'>
              <MapSwitcher appId={APP_TYPE.VYDEJ} />
            </div>
          </div>
        </MapWithStore>
      </div>
      <BtnBar>
        <CtaBtn icon={{ position: 'right', element: <SvgArrowRight /> }} onClick={onNextStepClick}>
          {config.hideReason ? (
            <FormattedMessage {...newOrderMessages.summaryNextBtnLabel} />
          ) : customerType === 'ipr' ? (
            <FormattedMessage {...newOrderMessages.detailsNextBtnLabel} />
          ) : (
            <FormattedMessage {...newOrderMessages.reasonNextBtnLabel} />
          )}
        </CtaBtn>
        <BtnBarSpan />
        <SecondaryBtn
          icon={{ position: 'left', element: <SvgArrowLeft /> }}
          onClick={onPrevStepClick}
        >
          <FormattedMessage {...newOrderMessages.publishSetsPrevBtnLabel} />
        </SecondaryBtn>
      </BtnBar>
    </div>
  );
};
